// This device's id (skill player-identity): a UUIDv7 generated on the first launch and kept in secure
// storage (iOS Keychain / Android Keystore; localStorage on the web, which is not secret). auth.login sends
// it on every connection; the device's first login creates its player. It is the device's login: never
// log it or put it in analytics.
import { KeychainAccess, SecureStorage } from "@aparajita/capacitor-secure-storage";
import type { DeviceIdSource } from "./net/session";

const DEVICE_ID_KEY = "deviceId";

/** A lowercase UUIDv7, the contract format of deviceId. */
export const DEVICE_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

/** RFC 9562 UUIDv7: 48-bit Unix ms timestamp, version 7, variant 10, 74 random bits. */
export function newUuidV7(nowMs: number = Date.now(), random: (bytes: Uint8Array<ArrayBuffer>) => void = (b) => crypto.getRandomValues(b)): string {
  const b = new Uint8Array(16);
  random(b);
  let ms = Math.floor(nowMs);
  for (let i = 5; i >= 0; i--) {
    b[i] = ms % 256;
    ms = Math.floor(ms / 256);
  }
  b[6] = (b[6]! & 0x0f) | 0x70;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const hex = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Key-value storage the device id is kept in. */
export interface DeviceIdStorage {
  get(): Promise<string | undefined>;
  set(id: string): Promise<void>;
}

/**
 * Returns the stored device id, or generates, stores and returns a new one when none is stored. A stored
 * value that is not a UUIDv7 is replaced. Rejects when storage can't be read or written, so a failing
 * keychain never silently turns the player into a new one.
 */
export function deviceIdSource(storage: DeviceIdStorage, generate: () => string = newUuidV7): DeviceIdSource {
  let pending: Promise<string> | undefined;
  const load = async () => {
    const stored = await storage.get();
    if (stored !== undefined && DEVICE_ID_PATTERN.test(stored)) return stored;
    const id = generate();
    await storage.set(id);
    return id;
  };
  return {
    getOrCreate() {
      // One at a time, so two connections of a first launch use one id; retried after a failure.
      pending ??= load().catch((err: unknown) => {
        pending = undefined;
        throw err;
      });
      return pending;
    },
  };
}

let configured: Promise<void> | undefined;

/** Readable after the first unlock, so a background launch can still log in. */
function configure(): Promise<void> {
  configured ??= SecureStorage.setDefaultKeychainAccess(KeychainAccess.afterFirstUnlock).catch(() => {});
  return configured;
}

const secureStorage: DeviceIdStorage = {
  async get() {
    await configure();
    const value = await SecureStorage.getItem(DEVICE_ID_KEY);
    return value ?? undefined;
  },
  async set(id) {
    await configure();
    await SecureStorage.setItem(DEVICE_ID_KEY, id);
  },
};

/** The app's device id, in secure storage. */
export const deviceId: DeviceIdSource = deviceIdSource(secureStorage);
